import type { CSSProperties } from 'react';

interface MentionVideoPosterFrameProps {
  videoUrl: string;
  /** Faded out, not unmounted, while a live player holds the same surface. */
  faded?: boolean;
  /** The browser could not decode the file — the caller decides what replaces it. */
  onError?: () => void;
}

const SURFACE: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

/**
 * The first frame of a video, drawn as a still.
 *
 * A platform video address is an MP4: an `<img>` renders nothing at all for it.
 * A muted `<video>` asked for metadata alone downloads the header rather than
 * the file, and the `#t=0.1` media fragment is what makes it paint that first
 * frame instead of a black rectangle.
 *
 * Shared by the dashboard's mention grid and by the Studio's mention library, so
 * the same trick is spelled once: the two surfaces would otherwise drift on the
 * fragment, which is the part that silently stops working when it is wrong.
 */
export function MentionVideoPosterFrame({
  videoUrl,
  faded = false,
  onError,
}: MentionVideoPosterFrameProps) {
  return (
    <video
      preload="metadata"
      muted
      playsInline
      onError={onError}
      style={{
        ...SURFACE,
        opacity: faded ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      {/* The handler is stated on both elements: a media element with `<source>`
          children never fires `error` on itself in some browsers, and fires only
          on itself in others. */}
      <source src={`${videoUrl}#t=0.1`} type="video/mp4" onError={onError} />
    </video>
  );
}

export default MentionVideoPosterFrame;
